import {
  ConditionOperator,
  FingerprintValueType,
  SlotDataType,
  ListItemType,
} from '../../types';

/**
 * Maps FingerprintValueType to valid ConditionOperators.
 * This ensures users only see relevant operations for the selected field type.
 */
export const operationsByValueType: Record<
  FingerprintValueType,
  ConditionOperator[]
> = {
  // Numeric types support comparison operators
  [FingerprintValueType.Number]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.GreaterThan,
    ConditionOperator.GreaterThanOrEqual,
    ConditionOperator.LessThan,
    ConditionOperator.LessThanOrEqual,
    ConditionOperator.Between,
    ConditionOperator.NotBetween,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Monetary values are numeric, same operators
  [FingerprintValueType.MonetaryValue]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.GreaterThan,
    ConditionOperator.GreaterThanOrEqual,
    ConditionOperator.LessThan,
    ConditionOperator.LessThanOrEqual,
    ConditionOperator.Between,
    ConditionOperator.NotBetween,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // String types support text matching operators
  [FingerprintValueType.String]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.Contains,
    ConditionOperator.NotContains,
    ConditionOperator.StartsWith,
    ConditionOperator.EndsWith,
    ConditionOperator.InList,
    ConditionOperator.NotInList,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Hash is a string type - exact match preferred
  [FingerprintValueType.Hash]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.StartsWith,
    ConditionOperator.EndsWith,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Boolean only supports equality checks
  [FingerprintValueType.Boolean]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Null type only supports null checks
  [FingerprintValueType.Null]: [
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],
};

/**
 * Maps SlotDataType to valid ConditionOperators.
 * Used by LogFingerprintEditor which works with SlotDataType instead of FingerprintValueType.
 */
export const operationsBySlotDataType: Record<
  SlotDataType,
  ConditionOperator[]
> = {
  // Numeric types
  [SlotDataType.Number]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.GreaterThan,
    ConditionOperator.GreaterThanOrEqual,
    ConditionOperator.LessThan,
    ConditionOperator.LessThanOrEqual,
    ConditionOperator.Between,
    ConditionOperator.NotBetween,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // String types
  [SlotDataType.String]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.Contains,
    ConditionOperator.NotContains,
    ConditionOperator.StartsWith,
    ConditionOperator.EndsWith,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Address is a hex string - exact/prefix matching, plus list membership
  [SlotDataType.Address]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.StartsWith,
    ConditionOperator.EndsWith,
    ConditionOperator.InList,
    ConditionOperator.NotInList,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Bytes are hex data - prefix/suffix matching useful
  [SlotDataType.Bytes]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.StartsWith,
    ConditionOperator.EndsWith,
    ConditionOperator.Contains,
    ConditionOperator.NotContains,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Topic is a 32-byte hex value
  [SlotDataType.Topic]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.StartsWith,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Array - element matching
  [SlotDataType.Array]: [
    ConditionOperator.Contains,
    ConditionOperator.NotContains,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],

  // Object - existence checks
  [SlotDataType.Object]: [
    ConditionOperator.Equals,
    ConditionOperator.NotEquals,
    ConditionOperator.IsNull,
    ConditionOperator.IsNotNull,
  ],
};

/**
 * Get valid operations for a FingerprintValueType
 */
export function getOperationsForValueType(
  valueType: FingerprintValueType,
): ConditionOperator[] {
  return (
    operationsByValueType[valueType] ||
    operationsByValueType[FingerprintValueType.String]
  );
}

/**
 * Get valid operations for a SlotDataType
 */
export function getOperationsForSlotDataType(
  slotType: SlotDataType,
): ConditionOperator[] {
  return (
    operationsBySlotDataType[slotType] ||
    operationsBySlotDataType[SlotDataType.String]
  );
}

/**
 * Format operator for display
 */
export function formatOperator(op: ConditionOperator): string {
  const labels: Record<ConditionOperator, string> = {
    [ConditionOperator.Equals]: 'Equals',
    [ConditionOperator.NotEquals]: 'Not Equals',
    [ConditionOperator.GreaterThan]: 'Greater Than',
    [ConditionOperator.GreaterThanOrEqual]: 'Greater Than or Equal',
    [ConditionOperator.LessThan]: 'Less Than',
    [ConditionOperator.LessThanOrEqual]: 'Less Than or Equal',
    [ConditionOperator.Between]: 'Between',
    [ConditionOperator.NotBetween]: 'Not Between',
    [ConditionOperator.Contains]: 'Contains',
    [ConditionOperator.NotContains]: 'Does Not Contain',
    [ConditionOperator.StartsWith]: 'Starts With',
    [ConditionOperator.EndsWith]: 'Ends With',
    [ConditionOperator.InList]: 'In List',
    [ConditionOperator.NotInList]: 'Not In List',
    [ConditionOperator.IsNull]: 'Is Null',
    [ConditionOperator.IsNotNull]: 'Is Not Null',
  };
  return labels[op] || op;
}

/**
 * Filter out null-related operations for non-nullable fields
 */
export function filterNullOperations(
  operations: ConditionOperator[],
  nullable: boolean,
): ConditionOperator[] {
  if (nullable) {
    return operations;
  }
  return operations.filter(
    (op) =>
      op !== ConditionOperator.IsNull && op !== ConditionOperator.IsNotNull,
  );
}

/**
 * Check if operator is a list-based operation (InList or NotInList)
 * These operations require a list_id reference instead of a regular value
 */
export function isListOperation(op: ConditionOperator): boolean {
  return op === ConditionOperator.InList || op === ConditionOperator.NotInList;
}

/**
 * Maps SlotDataType to the corresponding ListItemType for list-based operations
 * Returns null if the slot type doesn't support list operations
 */
export function getListItemTypeForSlot(
  slotType: SlotDataType,
): ListItemType | null {
  switch (slotType) {
    case SlotDataType.Address:
      return ListItemType.Address;
    // Future: add more mappings as needed
    // case SlotDataType.String:
    //   return ListItemType.String;
    default:
      return null;
  }
}
